import {useTranslation} from "@i18n/client";
import React from "react";
import Download from "src/components/v5/Reason/Download";
import TestimonialSlider from "src/components/v5/TestimonialSlider";
import {NextReduxWrapper, actions, useTypedSelector} from "src/store";

import {vcData} from "@constants/virtual-care";
import Insurances from "../components/v5/Insurances";
import Layout from "../components/v5/Layout";
import Hero from "../components/v5/VirtualCare/Hero";
import Reasons from "../components/v5/VirtualCare/Reasons";
import Services from "../components/v5/VirtualCare/Services";
import {SpecialtyId} from "../constants/specialtyIds";
import {loadAppointmentReasons} from "@services/monolith/appointmentReasons";
import {PracticeId} from "src/constants/practiceIds";
import {withLocaleServerSideProps} from "@i18n/pages";
import {useSortLocations} from "src/hooks/useSortLocations";
import useNextPagesPaths from "@hooks/app-pages-transition/useNextPagesPaths";

const currentSpecialtyId = SpecialtyId.VIRTUAL_URGENT_CARE;

const VirtualCare = () => {
  const i18n = useTranslation();

  const {reasons = [], locations: _locations, practices} = useTypedSelector(({config}) => config);
  const [locations, locationsSorted] = useSortLocations(_locations);
  const {pathname} = useNextPagesPaths();

  const carbonPractice = practices?.findById(PracticeId.CARBON);
  const {downloadDeepLink, slug: practiceSlug} = carbonPractice ?? {};

  return (
    <Layout
      pageTitle={i18n.t("Talk to a Doctor Online | Virtual Care at Carbon Health")}
      pageDescription={i18n.t(
        "From preventive to urgent care and everything in between, our virtual care providers are here for you. Connect with a doctor online today.",
      )}
      pageImage="virtual-care"
      navStartTransparent={false}
      style={{
        "--brandColor": vcData[currentSpecialtyId].color,
        "--brandColorDarken": vcData[currentSpecialtyId].darker,
        "--brandColorDarken2": vcData[currentSpecialtyId].darker2,
        "--hero": `linear-gradient(90deg, rgba(53, 56, 61, 0.8) 0%, rgba(70, 72, 73, 0) 75%), url(/static/img/v5/${vcData[currentSpecialtyId].hero}.jpg)`,
      }}
    >
      <Hero pathname={pathname} currentSpecialtyId={currentSpecialtyId} />
      <Services locations={locations} locationsSorted={locationsSorted} />
      <Download
        trackingLabel={currentSpecialtyId}
        downloadDeepLink={downloadDeepLink}
        practiceSlug={practiceSlug}
        locations={locations}
        locationsSorted={locationsSorted}
      />
      <Reasons {...{reasons, currentSpecialtyId}} />
      <Insurances t={i18n.t} />
      <TestimonialSlider />
    </Layout>
  );
};

export const getServerSideProps = withLocaleServerSideProps(
  NextReduxWrapper.getServerSideProps(store => async () => {
    const state = store.getState();

    const reasons = await (state.config.reasons
      ? Promise.resolve([])
      : loadAppointmentReasons(PracticeId.CARBON));

    const payload = {
      reasons: reasons.mapPick([
        "id",
        "slug",
        "name",
        "iconName",
        "description",
        "specialtyIds",
        "newPatientDuration",
        "supportsVideoVisit",
        "supportsInstantVisit",
        "practiceApptReasonSpecialties",
      ]),
    }.pickBy();

    store.dispatch(actions.setConfig(payload));

    return {props: {}};
  }),
);

export default VirtualCare;
